<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-2">
      <div>
        <!-- <h4 class="fw-bold mb-1">
          <i class="fa-solid fa-file-lines me-2 text-primary"></i>{{ $t('Reports') }}
        </h4> -->
        <small class="text-muted">{{ $t('Select a report to view details and analytics') }}</small>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReports">{{ $t('Retry') }}</button>
    </div>

    <!-- Search -->
    <div class="mb-4">
      <div class="input-group" style="max-width: 400px;">
        <span class="input-group-text bg-white border-end-0"><i class="fa-solid fa-search text-muted"></i></span>
        <input
          type="text"
          class="form-control border-start-0 ps-0"
          :placeholder="$t('Search reports') + '...'"
          v-model="searchQuery"
        />
      </div>
    </div>

    <!-- Report Categories -->
    <div v-for="category in filteredCategories" :key="category.code" class="mb-4">
      <h6 class="fw-bold text-uppercase small text-muted mb-3 d-flex align-items-center">
        <i :class="category.icon" class="me-2" :style="{ color: category.color }"></i>
        {{ $t(category.name) }}
        <span class="badge bg-light text-muted ms-2 fw-normal">{{ category.reports.length }}</span>
      </h6>
      <div class="row g-3">
        <div
          class="col-12 col-sm-6 col-lg-4 col-xl-3"
          v-for="report in category.reports"
          :key="report.code"
        >
          <router-link
            :to="{ name: routeNameOf(report.code) }"
            class="text-decoration-none"
          >
            <div class="card border-0 shadow-sm report-card h-100">
              <div class="card-body p-3">
                <div class="d-flex align-items-start gap-3">
                  <div class="report-icon-wrap" :style="{ backgroundColor: report.bg_color }">
                    <i :class="report.icon" :style="{ color: report.color }"></i>
                  </div>
                  <div class="flex-grow-1 min-w-0">
                    <div class="fw-semibold mb-1 text-truncate text-dark">{{ $t(report.name) }}</div>
                    <div class="text-muted small report-desc">{{ $t(report.description) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- No results -->
    <div v-if="filteredCategories.length === 0" class="text-center py-5">
      <i class="fa-solid fa-search fa-3x text-muted mb-3"></i>
      <h5 class="text-muted">{{ $t('No reports found') }}</h5>
      <p class="text-muted">{{ searchQuery.trim() ? $t('Try a different search term') : $t('No reports are enabled for this shop') }}</p>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';

/**
 * หน้ารวมรายงาน — รายการมาจาก API ล้วน ๆ (../silver-spoon-api/docs/report-catalog.md)
 *
 * เดิมลิสต์รายงานฮาร์ดโค้ดอยู่ในไฟล์นี้ ทุกร้านจึงเห็นการ์ดเท่ากันหมด
 * ตอนนี้ยิง GET /v1/reports ทุกครั้งที่เข้าหน้า → ร้านไหนปิดรายงานไหน การ์ดก็หายของร้านนั้น
 * (ไม่แคชไว้ที่ FE โดยตั้งใจ — เปิด/ปิดที่ DB แล้วต้องเห็นผลรอบถัดไปที่เข้าหน้า ไม่ต้องล้าง cache)
 *
 * name / description ที่ API ส่งมาเป็น "คีย์ i18n" ไม่ใช่คำแปล จึงต้องห่อ $t() เสมอ
 */
export default {
  name: 'ReportIndex',
  components: { PlaceholderContent },
  data() {
    return {
      searchQuery: '',
      pageLoading: true,
      errorMessage: '',
      categories: [],
    };
  },
  computed: {
    filteredCategories() {
      if (!this.searchQuery.trim()) return this.categories;
      const q = this.searchQuery.toLowerCase();
      // ค้นบนข้อความที่แปลแล้ว ผู้ใช้จะได้พิมพ์ไทยหาได้ (คีย์ดิบเป็นอังกฤษ)
      const match = (key) => this.$t(key).toLowerCase().includes(q);
      return this.categories
        .map(cat => ({
          ...cat,
          reports: cat.reports.filter(r => match(r.name) || match(r.description) || match(cat.name)),
        }))
        .filter(cat => cat.reports.length > 0);
    },
  },
  methods: {
    /**
     * code (snake_case ฝั่ง DB) → ชื่อ route ฝั่ง Vue (kebab-case)
     * mapping อยู่ที่เดียวตรงนี้ จึงไม่ต้องเก็บ route_name ใน DB ให้หลุด sync กับ router
     *   sales_summary → report.sales-summary
     */
    routeNameOf(code) {
      return 'report.' + String(code).replace(/_/g, '-');
    },

    async loadReports() {
      this.pageLoading = true;
      this.errorMessage = '';
      try {
        const res = await Reports.list();
        // กันกรณี API รู้จักรายงานที่ bundle นี้ยังไม่มีหน้า (แท็บเก่าค้างข้ามการ deploy)
        // ปล่อยให้ router-link ชี้ route ที่ไม่มีจริง = ทั้งหน้าพัง
        this.categories = (res.data.data || [])
          .map(group => ({
            ...group,
            reports: (group.reports || []).filter(r => this.$router.hasRoute(this.routeNameOf(r.code))),
          }))
          .filter(group => group.reports.length > 0);
      } catch (err) {
        this.categories = [];
        this.errorMessage = err?.response?.data?.message || err?.message || this.$t('Failed to load data');
      } finally {
        this.pageLoading = false;
      }
    },
  },
  mounted() {
    this.loadReports();
  },
};
</script>
